import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import {
  Star as StarIcon,
  Add as AddIcon,
  Restaurant as RestaurantIcon,
  Place as PlaceIcon,
  LocalActivity as ActivityIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getTripLocationsArray } from '../utils/tripUtils';
import { DOCUMENT, documentOutlineButtonSx } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';
import travelService from '../services/travelService';
import viatorService from '../services/viator';
import {
  ALL_TRIP_DESTINATIONS,
  mapViatorActivityToPlace,
  mergeViatorSearchResults,
  resolveViatorSearchLocations,
  tripLocationNames,
  viatorMultiAreaSearchUrl,
} from '../utils/topRatedViator';

// Alternative API fallback when Google APIs fail
const getAlternativePlacesData = async (type, location) => {
  try {
    // Try Foursquare API as alternative
    const response = await fetch(`/api/places/foursquare?type=${type}&location=${encodeURIComponent(location)}`);
    if (response.ok) {
      const data = await response.json();
      return data.places || [];
    }
  } catch (error) {
    console.warn('Foursquare API failed:', error);
  }
  
  try {
    // Try OpenStreetMap Nominatim as fallback
    const response = await fetch(`/api/places/nominatim?type=${type}&location=${encodeURIComponent(location)}`);
    if (response.ok) {
      const data = await response.json();
      return data.places || [];
    }
  } catch (error) {
    console.warn('Nominatim API failed:', error);
  }
  
  return [];
};

const TopRatedSection = ({ trip, onAddActivity, isCreator, onCreateSuggestion, isActive = false }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [topRated, setTopRated] = useState({});
  const [expandedLocations, setExpandedLocations] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(ALL_TRIP_DESTINATIONS);

  // Get trip locations for search
  const getTripLocations = useCallback(() => {
    if (!trip) return [{ name: 'Dubai, UAE', coordinates: null }];
    
    const locations = getTripLocationsArray(trip);
    return locations.length > 0 ? locations : [{ name: 'Dubai, UAE', coordinates: null }];
  }, [trip]);

  const searchTopRatedPlaces = useCallback(async (type, location) => getAlternativePlacesData(type, location), []);

  // Load top-rated places
  useEffect(() => {
    const loadTopRated = async () => {
      if (!isActive || !trip) return;
      
      setLoading(true);
      setError(null);

      try {
        const locations = getTripLocations();

        // Initialize expanded locations (first location expanded by default)
        if (locations.length > 0) {
          setExpandedLocations(new Set([locations[0].name]));
        }

        // Search for each location
        const locationResults = {};
        
        for (const location of locations) {
          const locationName = location.name;
          
          // Search for different types of places with individual error handling
          const searchPromises = [
            searchTopRatedPlaces('attractions', locationName).catch(async err => {
              console.warn(`Failed to load attractions for ${locationName}:`, err);
              console.log('Trying alternative APIs for attractions...');
              return await getAlternativePlacesData('attractions', locationName);
            }),
            searchTopRatedPlaces('restaurants', locationName).catch(async err => {
              console.warn(`Failed to load restaurants for ${locationName}:`, err);
              console.log('Trying alternative APIs for restaurants...');
              return await getAlternativePlacesData('restaurants', locationName);
            }),
            searchTopRatedPlaces('things to do', locationName).catch(async err => {
              console.warn(`Failed to load activities for ${locationName}:`, err);
              console.log('Trying alternative APIs for activities...');
              return await getAlternativePlacesData('activities', locationName);
            })
          ];

          // Add a timeout for each location
          const locationTimeout = setTimeout(() => {
            console.warn(`Search operation timed out for ${locationName}`);
          }, 30000); // 30 seconds per location

          const [attractions, restaurants, activities] = await Promise.all(searchPromises);
          clearTimeout(locationTimeout);

          locationResults[locationName] = {
            attractions: attractions || [],
            restaurants: restaurants || [],
            activities: activities || []
          };
        }

        setTopRated(locationResults);

        // Use Viator API for Top Attractions - call API for each location
        if (locations.length > 0) {
          // Process each location individually
          for (const location of locations) {
            try {
              const searchLocation = location.name;
              console.log(`🔍 TopRatedSection: Getting Viator attractions for location: "${searchLocation}"`);
              // Use travel service for activity recommendations (handles destination ID conversion)
              const viatorActivities = await travelService.getActivityRecommendations(searchLocation, 10);
              console.log(`🔍 TopRatedSection: Received ${viatorActivities.length} Viator activities for ${searchLocation}`);
              const viatorAttractions = viatorActivities.map(mapViatorActivityToPlace);
              
              // Update the topRated state for this specific location
              setTopRated(prev => {
                const updated = { ...prev };
                if (!updated[location.name]) {
                  updated[location.name] = { attractions: [], restaurants: [], hotels: [] };
                }
                updated[location.name].attractions = viatorAttractions;
                return updated;
              });
              
            } catch (viatorError) {
              console.warn(`Viator search failed for ${location.name}:`, viatorError);
            }
          }
        }

        // Use Duffel API for Top Hotels with Booking.com fallback - call API for each location
        if (locations.length > 0 && trip.start_date && trip.end_date) {
          const formatDate = (dateString) => {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          };
          
          // Process each location individually
          for (const location of locations) {
            try {
              const searchLocation = location.name;
              console.log(`🔍 TopRatedSection: Getting hotels for location: "${searchLocation}"`);
              
              // Use travel service for hotel recommendations (handles location conversion)
              let hotels = await travelService.getHotelRecommendations({
                location: searchLocation,
                check_in: formatDate(trip.start_date),
                check_out: formatDate(trip.end_date),
                guests: 1
              });
              console.log(`🔍 TopRatedSection: Travel service returned ${hotels.length} hotels for ${searchLocation}`);
              
              // Fallback to Booking.com if Duffel returns no results
              if (hotels.length === 0) {
                const checkIn = formatDate(trip.start_date);
                const checkOut = formatDate(trip.end_date);
                const bookingUrl = `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(searchLocation)}&checkin=${checkIn}&checkout=${checkOut}&group_adults=1&no_rooms=1&utm_source=dejavu-nomad&utm_medium=affiliate&utm_campaign=top-rated`;
                
                hotels = [{
                  id: 'booking_search',
                  name: `Hotels in ${searchLocation}`,
                  location: searchLocation,
                  rating: 4.5,
                  price: 'From $50',
                  currency: 'USD',
                  check_in: checkIn,
                  check_out: checkOut,
                  guests: 1,
                  amenities: ['WiFi', 'Pool', 'Restaurant', 'Spa'],
                  image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400',
                  source: 'booking',
                  url: bookingUrl,
                  description: `Find the best hotels in ${searchLocation} for your stay`
                }];
              }
              
              // Update the topRated state for this specific location
              setTopRated(prev => {
                const updated = { ...prev };
                if (!updated[location.name]) {
                  updated[location.name] = { attractions: [], restaurants: [], hotels: [] };
                }
                updated[location.name].hotels = hotels;
                return updated;
              });
              
            } catch (hotelError) {
              console.warn(`Hotel search failed for ${location.name}:`, hotelError);
            }
          }
        }
      } catch (err) {
        console.error('Error loading top-rated places:', err);
        setError('Failed to load top-rated places');
      } finally {
        setLoading(false);
      }
    };

    loadTopRated();
  }, [isActive, trip, getTripLocations, searchTopRatedPlaces]);

  useEffect(() => {
    const names = tripLocationNames(getTripLocations());
    if (selectedDestination !== ALL_TRIP_DESTINATIONS && !names.includes(selectedDestination)) {
      setSelectedDestination(ALL_TRIP_DESTINATIONS);
    }
  }, [trip, getTripLocations, selectedDestination]);

  const tripLocations = getTripLocations();
  const destinationNames = tripLocationNames(tripLocations);
  const hasMultipleDestinations = destinationNames.length > 1;
  const searchLocations = resolveViatorSearchLocations(tripLocations, selectedDestination);
  const searchPlaceholderLocation = searchLocations.length === 1
    ? searchLocations[0]
    : destinationNames.join(', ');
  const viatorBrowseUrl = viatorMultiAreaSearchUrl(tripLocations, searchQuery, selectedDestination);

  const handleViatorSearch = async (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query || !searchLocations.length) return;

    setSearching(true);
    setSearchError(null);
    setHasSearched(true);
    try {
      const perCityLimit = searchLocations.length > 1 ? 8 : 10;
      const groups = await Promise.all(searchLocations.map(async (location) => {
        const response = await viatorService.search(query, location, perCityLimit);
        return { location, activities: response?.activities || [] };
      }));
      setSearchResults(mergeViatorSearchResults(groups));
    } catch (err) {
      console.warn('Viator area search failed:', err);
      setSearchResults([]);
      setSearchError(t('itinerary.topRatedSearchFailed'));
    } finally {
      setSearching(false);
    }
  };

  // Toggle location expansion
  const toggleLocation = (locationName) => {
    setExpandedLocations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationName)) {
        newSet.delete(locationName);
      } else {
        newSet.add(locationName);
      }
      return newSet;
    });
  };

  // Get photo URL for a place
  const getPhotoUrl = (place) => {
    if (place.photos && place.photos.length > 0) {
      const photo = place.photos[0];
      // Handle both old and new API photo formats
      if (typeof photo.getUrl === 'function') {
        return photo.getUrl({ maxWidth: 400, maxHeight: 300 });
      } else if (photo.name && !String(photo.name).startsWith('places/')) {
        return null;
      }
    }
    return null;
  };

  // Get category icon
  const getCategoryIcon = (type) => {
    if (type === 'restaurants') return <RestaurantIcon />;
    if (type === 'attractions') return <PlaceIcon />;
    return <ActivityIcon />;
  };

  // Get category color
  const getCategoryColor = (type) => {
    if (type === 'restaurants') return '#ed6c02';
    if (type === 'attractions') return '#9c27b0';
    return '#2e7d32';
  };

  // Handle add to itinerary
  const handleAddToItinerary = (place, category) => {
    const activityData = {
      name: place.displayName || place.name,
      location: place.formattedAddress || place.formatted_address,
      google_map_link: `https://www.google.com/maps/place/?q=place_id:${place.place_id || place.id}`,
      category: category === 'restaurants' ? 'restaurant' : 'activity',
      description: `Top-rated ${category} with ${place.rating} stars`
    };

    if (isCreator) {
      onAddActivity(activityData);
    } else {
      onCreateSuggestion(activityData);
    }
  };

  // Render place card
  const renderPlaceCard = (place, category) => {
    const photoUrl = getPhotoUrl(place);
    const categoryIcon = getCategoryIcon(category);
    const categoryColor = getCategoryColor(category);
    
    // Handle both old and new API field names
    const placeId = place.place_id || place.id;
    const placeName = place.displayName || place.name;
    const userRatingsTotal = place.user_ratings_total || place.userRatingCount;
    
    // Use the URL from the place data (Viator affiliate link) or fallback to Google Maps
    const placeUrl = place.url || `https://www.google.com/maps/place/?q=place_id:${placeId}`;

    return (
      <Card 
        sx={{ 
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            '& .floating-add-button': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}
      >
        {photoUrl && (
          <CardMedia
            component="img"
            height="200"
            image={photoUrl}
            alt={placeName}
            sx={{ 
              objectFit: 'cover',
              borderRadius: '8px 8px 0 0',
              imageRendering: 'high-quality',
              WebkitImageRendering: 'high-quality',
              MozImageRendering: 'high-quality',
              msImageRendering: 'high-quality'
            }}
          />
        )}
        
        {/* Category Icon Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: categoryColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          {categoryIcon}
        </Box>

        <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Rating - Top priority */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <StarIcon sx={{ color: '#00a651', fontSize: 16, mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, mr: 0.5, color: '#00a651' }}>
              {place.rating != null ? Number(place.rating).toFixed(1) : '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              {place.rating != null
                ? t('itinerary.topRatedReviews', { count: userRatingsTotal || 0 })
                : t('itinerary.topRatedNoRating')}
            </Typography>
          </Box>

          {/* Title */}
          <Typography 
            variant="h6" 
            component="a"
            href={placeUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              fontWeight: 700, 
              mb: 1,
              color: '#1a1a1a',
              textDecoration: 'none',
              cursor: 'pointer',
              fontSize: '1rem',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              '&:hover': {
                color: 'primary.main'
              }
            }}
          >
            {placeName}
          </Typography>
          {place.destinationName ? (
            <Typography sx={{ mb: 1, fontSize: 12, color: DOCUMENT.ink3 }}>
              {place.destinationName}
            </Typography>
          ) : null}

          {/* Features for Viator activities */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
            {place.duration && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  ⏱️ {typeof place.duration === 'object' ? 
                    `${Math.round((place.duration.variableDurationFromMinutes || 0) / 60)} hours` : 
                    place.duration
                  }
                </Typography>
              </Box>
            )}
            {place.freeCancellation ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {t('itinerary.topRatedFreeCancellation')}
              </Typography>
            </Box>
            ) : null}
          </Box>

          {/* Pricing - Prominent display */}
          {place.price && (
            <Box sx={{ 
              mt: 'auto', 
              pt: 1, 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between'
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.8rem'
                }}
              >
                from
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#1a1a1a',
                  fontWeight: 700,
                  fontSize: '1.4rem'
                }}
              >
                {typeof place.price === 'string' ? place.price.replace('From ', '') : place.price}
              </Typography>
            </Box>
          )}

          {/* Floating Add Button - Only visible on hover */}
          <IconButton
            className="floating-add-button"
            onClick={() => handleAddToItinerary(place, category)}
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              borderRadius: '50%',
              width: 56,
              height: 56,
              backgroundColor: 'primary.main',
              color: 'white',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              opacity: 0,
              transform: 'translateY(8px)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.3)'
              }
            }}
          >
            <AddIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </CardContent>
      </Card>
    );
  };

  // Render section
  const renderSection = (title, places, category) => {
    if (!places || places.length === 0) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {getCategoryIcon(category)}
          <Typography variant="h5" sx={{ fontWeight: 600, ml: 1 }}>
            {title}
          </Typography>
          <Chip 
            label={`${places.length} places`}
            size="small"
            sx={{ ml: 2, backgroundColor: getCategoryColor(category), color: 'white' }}
          />
        </Box>
        
        <Grid container spacing={2}>
          {places.map(place => {
            const placeId = place.place_id || place.id;
            return (
              <Grid item xs={12} sm={6} md={4} key={placeId}>
                {renderPlaceCard(place, category)}
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <DocumentSectionHead title={t('itinerary.tabs.topRated')} meta={t('itinerary.topRatedMeta')} />

      <Box
        component="form"
        onSubmit={handleViatorSearch}
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        {hasMultipleDestinations ? (
          <TextField
            select
            size="small"
            value={selectedDestination}
            onChange={(event) => setSelectedDestination(event.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                bgcolor: DOCUMENT.surface,
              },
            }}
            inputProps={{ 'aria-label': t('itinerary.topRatedDestination') }}
          >
            <MenuItem value={ALL_TRIP_DESTINATIONS}>{t('itinerary.topRatedAllDestinations')}</MenuItem>
            {destinationNames.map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={hasMultipleDestinations && selectedDestination === ALL_TRIP_DESTINATIONS
            ? t('itinerary.topRatedSearchPlaceholderAll')
            : t('itinerary.topRatedSearchPlaceholder', { location: searchPlaceholderLocation })}
          size="small"
          fullWidth
          inputProps={{ 'aria-label': t('itinerary.topRatedSearch') }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              bgcolor: DOCUMENT.surface,
            },
          }}
        />
        <Button type="submit" disabled={searching || !searchQuery.trim()} sx={documentOutlineButtonSx}>
          {searching ? t('itinerary.topRatedSearching') : t('itinerary.topRatedSearch')}
        </Button>
        {viatorBrowseUrl ? (
          <Button
            component="a"
            href={viatorBrowseUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={documentOutlineButtonSx}
          >
            {t('itinerary.topRatedBrowse')}
          </Button>
        ) : null}
      </Box>

      {searchError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>
      ) : null}

      {hasSearched ? (
        searching ? (
          <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
            <CircularProgress size={20} />
            <Typography sx={{ ml: 1.5 }}>{t('itinerary.topRatedSearching')}</Typography>
          </Box>
        ) : (
          renderSection(
            t('itinerary.topRatedSearchResults'),
            searchResults,
            'attractions'
          ) || (
            <Typography sx={{ mb: 3, color: DOCUMENT.ink2, fontSize: 14 }}>
              {t('itinerary.topRatedNoSearchResults')}
            </Typography>
          )
        )
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t('itinerary.topRatedLoading')}</Typography>
        </Box>
      ) : Object.keys(topRated).length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {t('itinerary.topRatedEmpty')}
          </Typography>
        </Box>
      ) : (
        // All locations use accordion (single or multiple)
        Object.entries(topRated).map(([locationName, locationData]) => (
          <Accordion 
            key={locationName}
            expanded={expandedLocations.has(locationName)}
            onChange={() => toggleLocation(locationName)}
            elevation={0}
            sx={{ mb: 1, bgcolor: 'transparent', '&:before': { display: 'none' }, boxShadow: 'none' }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: DOCUMENT.ink3 }} />}
              sx={{
                bgcolor: 'transparent',
                color: DOCUMENT.ink,
                px: 0,
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18 }}>
                  {locationName}
                </Typography>
                <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                  {`${(locationData.attractions?.length || 0) + (locationData.restaurants?.length || 0) + (locationData.activities?.length || 0)} places`}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {renderSection(t('itinerary.topRatedAttractions'), locationData.attractions, 'attractions')}
              {renderSection(t('itinerary.topRatedRestaurants'), locationData.restaurants, 'restaurants')}
              {renderSection(t('itinerary.topRatedHotels'), locationData.hotels, 'hotels')}
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default TopRatedSection;
